import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import axios from "axios";
import { 
  RefreshCw,
  Loader2,
  User,
  Copy,
  ExternalLink,
  ChevronDown,
  Check,
  MessageSquare,
  TrendingUp,
  Home,
  Shield,
  Plus,
  Trash2,
  Wallet,
  Download
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TEMPLATES = [
  { id: 'main', name: 'Главная', icon: Home, description: 'Баланс и обзор' },
  { id: 'trades', name: 'Сделки', icon: TrendingUp, description: 'P2P торговля' },
  { id: 'wallet', name: 'Кошелёк', icon: Wallet, description: 'История платежей' },
  { id: 'profile', name: 'Профиль', icon: User, description: 'Персональные данные' }
];

// Random trader names
const RANDOM_TRADERS = [
  { name: 'CryptoKing_Pro', avatar: 'CK' },
  { name: 'BitMaster2024', avatar: 'BM' },
  { name: 'TradeHunter', avatar: 'TH' },
  { name: 'P2P_Expert', avatar: 'PE' },
  { name: 'SafeTrader_RU', avatar: 'ST' },
  { name: 'CoinDealer777', avatar: 'CD' },
  { name: 'FastExchange', avatar: 'FE' },
  { name: 'TrustCrypto', avatar: 'TC' },
];

export default function BybitTemplate() {
  const [profiles, setProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState('main');
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedData, setGeneratedData] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showBrowser, setShowBrowser] = useState(true);
  const [shuffleTransactions, setShuffleTransactions] = useState(false);
  const [addRandomOperations, setAddRandomOperations] = useState(false); // Add 3-6 random operations
  const [isDownloadingArchive, setIsDownloadingArchive] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  // Custom trades/conversations
  const [trades, setTrades] = useState([]);
  // Wallet transactions
  const [transactions, setTransactions] = useState([]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  };

  // Generate random date
  const generateRandomDate = (daysAgo = null) => {
    const now = new Date();
    const days = daysAgo !== null ? daysAgo : Math.floor(Math.random() * 30);
    const date = new Date(now - days * 24 * 60 * 60 * 1000);
    return date.toLocaleString('ru-RU', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Generate date between two dates
  const generateDateBetween = (date1Str, date2Str) => {
    const parseDate = (dateStr) => {
      if (!dateStr) return new Date();
      const match = dateStr.match(/(\d{2})\.(\d{2})\.(\d{4}),?\s*(\d{2}):(\d{2})/);
      if (match) {
        const [, day, month, year, hour, min] = match;
        return new Date(year, month - 1, day, hour, min);
      }
      return new Date();
    };
    const date1 = parseDate(date1Str);
    const date2 = parseDate(date2Str);
    const minTime = Math.min(date1.getTime(), date2.getTime());
    const maxTime = Math.max(date1.getTime(), date2.getTime());
    const randomTime = minTime + Math.random() * (maxTime - minTime);
    const date = new Date(randomTime);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hour = String(date.getHours()).padStart(2, '0');
    const min = String(date.getMinutes()).padStart(2, '0');
    return `${day}.${month}.${year}, ${hour}:${min}`;
  };

  // Random crypto payment types
  const CRYPTO_PAYMENTS = [
    { name: 'Binance P2P', icon: '🔶' },
    { name: 'OKX Transfer', icon: '⚫' },
    { name: 'Bybit Deposit', icon: '🟠' },
    { name: 'MEXC Trade', icon: '🔵' },
    { name: 'KuCoin Exchange', icon: '🟢' },
    { name: 'Huobi Transfer', icon: '🔷' },
    { name: 'Gate.io', icon: '⬜' },
    { name: 'Crypto.com', icon: '🔹' },
  ];

  // Generate random trades (10-15)
  const generateRandomTrades = () => {
    const numTrades = Math.floor(Math.random() * 6) + 10; // 10-15 trades
    const newTrades = [];
    const cryptos = ['USDT', 'BTC', 'ETH', 'USDC'];
    
    const conversationTemplates = [
      [
        { from: 'partner', text: 'Здравствуйте! Хочу купить крипту' },
        { from: 'me', text: 'Добрый день! Готов к сделке, переводите' },
        { from: 'partner', text: 'Перевёл оплату, проверьте' },
        { from: 'me', text: 'Получил, отправляю крипту' },
        { from: 'partner', text: 'Всё получил, спасибо!' },
      ],
      [
        { from: 'partner', text: 'Привет! Готов к обмену?' },
        { from: 'me', text: 'Да, курс актуальный' },
        { from: 'partner', text: 'Отправил деньги на карту' },
        { from: 'me', text: 'Вижу перевод, отправляю USDT' },
        { from: 'partner', text: 'Получил, отличная сделка!' },
      ],
      [
        { from: 'partner', text: 'Добрый день! Нужен обмен' },
        { from: 'me', text: 'Здравствуйте! Какую сумму?' },
        { from: 'partner', text: 'Перевожу сейчас' },
        { from: 'me', text: 'Принял, отправляю крипту' },
        { from: 'partner', text: 'Спасибо, всё пришло!' },
      ],
    ];
    
    for (let i = 0; i < numTrades; i++) {
      const trader = RANDOM_TRADERS[Math.floor(Math.random() * RANDOM_TRADERS.length)];
      const crypto = cryptos[Math.floor(Math.random() * cryptos.length)];
      const amount = Math.floor(Math.random() * 80000) + 15000; // 15000-95000
      const template = conversationTemplates[Math.floor(Math.random() * conversationTemplates.length)];
      
      newTrades.push({
        id: Date.now() + i,
        partnerName: trader.name,
        amount: String(amount),
        crypto,
        messages: [...template],
        status: 'completed',
        date: generateRandomDate(i * 2 + Math.floor(Math.random() * 3))
      });
    }
    
    return newTrades;
  };

  // Generate transactions from OZON profile (only OZON payments by default)
  // If addRandomOperations is enabled, adds 3-6 random deposit/withdrawal operations
  const generateTransactions = (profile, overrideAddRandom = null) => {
    const useAddRandom = overrideAddRandom !== null ? overrideAddRandom : addRandomOperations;
    
    const ozonTransactions = profile?.transactions || [];
    const ozonList = [];
    
    // Determine method based on category
    const getPaymentMethod = (category) => {
      const sbpCategories = ['Переводы', 'Пополнения', 'Перевод'];
      const cardCategories = ['Финансовые услуги'];
      
      if (sbpCategories.some(c => category?.includes(c))) {
        return 'Перевод по СБП';
      } else if (cardCategories.some(c => category?.includes(c))) {
        return 'Перевод по карте';
      }
      return 'Перевод по СБП'; // default
    };
    
    // OZON Deposits → P2P Withdrawals (выводы)
    const deposits = ozonTransactions.filter(t => t.type === 'deposit');
    deposits.forEach((tx, i) => {
      const paymentMethod = getPaymentMethod(tx.category);
      ozonList.push({
        id: tx.id || Date.now() + i,
        type: 'withdrawal',
        description: 'Вывод средств',
        method: paymentMethod,
        sender: tx.method || 'Получатель',
        amount: tx.amount,
        crypto: 'RUB',
        status: 'completed',
        date: tx.date && tx.time ? `${tx.date}, ${tx.time}` : ''
      });
    });
    
    // OZON Withdrawals → P2P Deposits (пополнения)
    const withdrawals = ozonTransactions.filter(t => t.type === 'withdrawal');
    withdrawals.forEach((tx, i) => {
      const paymentMethod = getPaymentMethod(tx.category);
      ozonList.push({
        id: tx.id || Date.now() + 500 + i,
        type: 'deposit',
        description: 'Пополнение счёта',
        method: paymentMethod,
        sender: tx.method || 'Отправитель',
        amount: tx.amount,
        crypto: 'RUB',
        status: 'completed',
        date: tx.date && tx.time ? `${tx.date}, ${tx.time}` : ''
      });
    });
    
    // Sort OZON transactions by date
    const parseDate = (dateStr) => {
      if (!dateStr) return new Date(0);
      const match = dateStr.match(/(\d{2})\.(\d{2})\.(\d{4}),?\s*(\d{2}):(\d{2})/);
      if (match) {
        const [, day, month, year, hour, min] = match;
        return new Date(year, month - 1, day, hour, min);
      }
      return new Date(0);
    };
    
    ozonList.sort((a, b) => parseDate(b.date) - parseDate(a.date));
    
    // By default show only OZON payments
    // Add random operations if enabled
    if (useAddRandom) {
      return addRandomOps(ozonList);
    }
    
    return ozonList;
  };
  
  // Add 3-6 random operations BETWEEN existing payments
  // Methods: only "Перевод по СБП" or "Перевод по карте"
  // First (bottom) operation is always positive deposit up to 6000
  const addRandomOps = (transactionsList) => {
    if (transactionsList.length < 2) {
      // Not enough transactions to insert between
      return transactionsList;
    }
    
    const numOps = Math.floor(Math.random() * 4) + 3; // 3-6 operations
    const methods = ['Перевод по СБП', 'Перевод по карте'];
    const senders = ['Иванов И.И.', 'Петров П.П.', 'Сидоров С.С.', 'Козлов К.К.', 'Морозов М.М.'];
    
    // Parse date from string
    const parseDate = (dateStr) => {
      if (!dateStr) return new Date();
      const match = dateStr.match(/(\d{2})\.(\d{2})\.(\d{4}),?\s*(\d{2}):(\d{2})/);
      if (match) {
        const [, day, month, year, hour, min] = match;
        return new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hour), parseInt(min));
      }
      return new Date();
    };
    
    // Format date to string
    const formatDate = (date) => {
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hour = String(date.getHours()).padStart(2, '0');
      const min = String(date.getMinutes()).padStart(2, '0');
      return `${day}.${month}.${year}, ${hour}:${min}`;
    };
    
    // Generate date between two dates
    const generateDateBetween = (dateStr1, dateStr2) => {
      const date1 = parseDate(dateStr1);
      const date2 = parseDate(dateStr2);
      const minTime = Math.min(date1.getTime(), date2.getTime());
      const maxTime = Math.max(date1.getTime(), date2.getTime());
      const randomTime = minTime + Math.random() * (maxTime - minTime);
      return formatDate(new Date(randomTime));
    };
    
    // Sort existing transactions by date (oldest first for easier insertion)
    const sortedTransactions = [...transactionsList].sort((a, b) => parseDate(a.date) - parseDate(b.date));
    
    // Create random operations to insert between existing ones
    const randomOps = [];
    let hasDeposit = false;
    let hasWithdrawal = false;
    
    for (let i = 0; i < numOps; i++) {
      // Ensure at least one deposit and one withdrawal
      let isDeposit;
      if (i === numOps - 1 && !hasWithdrawal) {
        isDeposit = false;
      } else if (i === numOps - 1 && !hasDeposit) {
        isDeposit = true;
      } else {
        isDeposit = Math.random() > 0.5;
      }
      
      if (isDeposit) hasDeposit = true;
      else hasWithdrawal = true;
      
      // For the last random op (will be at bottom), make it a small positive deposit
      const isLastOp = i === numOps - 1;
      const amount = isLastOp && isDeposit 
        ? Math.floor(Math.random() * 5000) + 1000  // 1000-6000 for last deposit
        : Math.floor(Math.random() * 15000) + 2000; // 2000-17000 for others
      
      // Pick a random position between existing transactions
      const insertIdx = Math.floor(Math.random() * (sortedTransactions.length - 1));
      const prevTx = sortedTransactions[insertIdx];
      const nextTx = sortedTransactions[insertIdx + 1];
      
      randomOps.push({
        id: Date.now() + Math.random() * 10000 + i,
        type: isDeposit ? 'deposit' : 'withdrawal',
        description: isDeposit ? 'Пополнение счёта' : 'Вывод средств',
        method: methods[Math.floor(Math.random() * methods.length)],
        sender: senders[Math.floor(Math.random() * senders.length)],
        amount,
        crypto: 'RUB',
        status: 'completed',
        date: generateDateBetween(prevTx.date, nextTx.date)
      });
    }
    
    // Combine all transactions and sort by date (newest first, oldest last/bottom)
    const allTransactions = [...transactionsList, ...randomOps];
    allTransactions.sort((a, b) => parseDate(b.date) - parseDate(a.date));
    
    // Ensure the LAST element (bottom when displayed) is a positive deposit up to 6000
    const lastIdx = allTransactions.length - 1;
    const lastTx = allTransactions[lastIdx];
    
    if (lastTx.type !== 'deposit' || lastTx.amount > 6000) {
      // Don't modify existing transaction - ADD a new deposit at the end
      // Generate a date slightly before the current oldest transaction
      const oldestDate = parseDate(lastTx.date);
      const newDate = new Date(oldestDate.getTime() - Math.random() * 3600000 - 60000); // 1 min to 1 hour before
      
      allTransactions.push({
        id: Date.now() + Math.random() * 10000 + 999,
        type: 'deposit',
        description: 'Пополнение счёта',
        method: methods[Math.floor(Math.random() * methods.length)],
        sender: senders[Math.floor(Math.random() * senders.length)],
        amount: Math.floor(Math.random() * 5000) + 1000, // 1000-6000
        crypto: 'RUB',
        status: 'completed',
        date: formatDate(newDate)
      });
    }
    
    return allTransactions;
  };

  // Handle profile selection - auto-generate trades and transactions
  const handleSelectProfile = (profile) => {
    setSelectedProfile(profile);
    setDropdownOpen(false);
    setGeneratedData(null);
    
    // Generate random trades (10-15)
    const randomTrades = generateRandomTrades();
    setTrades(randomTrades);
    
    // Generate transactions from OZON profile (with or without crypto exchanges)
    const mixedTransactions = generateTransactions(profile);
    setTransactions(mixedTransactions);
    
    const depositCount = mixedTransactions.filter(t => t.type === 'deposit').length;
    const withdrawalCount = mixedTransactions.filter(t => t.type === 'withdrawal').length;
    toast.success(`${randomTrades.length} сделок, ${depositCount} пополнений, ${withdrawalCount} выводов`);
  };

  // Regenerate transactions when onlyOzonPayments changes
  const regenerateTransactions = () => {
    if (selectedProfile) {
      const mixedTransactions = generateTransactions(selectedProfile);
      setTransactions(mixedTransactions);
      setGeneratedData(null);
    }
  };

  const fetchProfiles = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/ozon/profiles`, {
        headers: getAuthHeaders(),
      });
      setProfiles(response.data);
    } catch (error) {
      console.error("Error fetching profiles:", error);
      toast.error("Ошибка загрузки профилей");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  const addTrade = () => {
    if (trades.length >= 15) {
      toast.error("Максимум 15 сделок");
      return;
    }
    const randomTrader = RANDOM_TRADERS[Math.floor(Math.random() * RANDOM_TRADERS.length)];
    setTrades([...trades, {
      id: Date.now(),
      partnerName: randomTrader.name,
      amount: String(Math.floor(Math.random() * 50000) + 10000),
      crypto: ['USDT', 'BTC', 'ETH'][Math.floor(Math.random() * 3)],
      messages: [
        { from: 'partner', text: 'Привет! Хочу обменять крипту' },
        { from: 'me', text: 'Здравствуйте, готов к сделке' },
        { from: 'partner', text: 'Отправил оплату' },
        { from: 'me', text: 'Получил, отправляю крипту' },
        { from: 'partner', text: 'Всё получил, спасибо!' },
      ],
      status: 'completed',
      date: ''
    }]);
  };

  const removeTrade = (id) => {
    if (trades.length <= 1) return;
    setTrades(trades.filter(t => t.id !== id));
  };

  const updateTrade = (id, field, value) => {
    setTrades(trades.map(t => t.id === id ? { ...t, [field]: value } : t));
  };

  const updateTradeMessage = (tradeId, msgIndex, text) => {
    setTrades(trades.map(t => {
      if (t.id === tradeId) {
        const newMessages = [...t.messages];
        newMessages[msgIndex] = { ...newMessages[msgIndex], text };
        return { ...t, messages: newMessages };
      }
      return t;
    }));
  };

  const addMessage = (tradeId, from) => {
    setTrades(trades.map(t => {
      if (t.id === tradeId) {
        return { ...t, messages: [...t.messages, { from, text: '' }] };
      }
      return t;
    }));
  };

  const removeMessage = (tradeId, msgIndex) => {
    setTrades(trades.map(t => {
      if (t.id === tradeId && t.messages.length > 1) {
        const newMessages = t.messages.filter((_, i) => i !== msgIndex);
        return { ...t, messages: newMessages };
      }
      return t;
    }));
  };

  const handleGenerate = () => {
    if (!selectedProfile) {
      toast.error("Выберите профиль OZON");
      return;
    }
    
    setIsGenerating(true);
    
    const firstName = selectedProfile.first_name || selectedProfile.name?.split(' ')[0] || "Имя";
    const lastName = selectedProfile.last_name || selectedProfile.name?.split(' ')[1] || "Фамилия";
    const phone = selectedProfile.phone || "+7 (999) 123-45-67";
    const email = selectedProfile.email !== "не установлен" ? selectedProfile.email : `${firstName.toLowerCase()}@mail.ru`;
    
    // Generate trades with dates and random partners
    const generatedTrades = trades.map((trade, index) => ({
      ...trade,
      partnerName: trade.partnerName || RANDOM_TRADERS[Math.floor(Math.random() * RANDOM_TRADERS.length)].name,
      date: trade.date || generateRandomDate(index * 2),
      amount: trade.amount || String(Math.floor(Math.random() * 50000) + 10000)
    }));
    
    // Calculate totals
    const totalDeposits = transactions.filter(t => t.type === 'deposit').reduce((sum, t) => sum + (t.amount || 0), 0);
    const totalWithdrawals = transactions.filter(t => t.type === 'withdrawal').reduce((sum, t) => sum + (t.amount || 0), 0);
    
    // Calculate date range from transactions for filter
    const parseDate = (dateStr) => {
      if (!dateStr) return null;
      const match = dateStr.match(/(\d{2})\.(\d{2})\.(\d{4})/);
      if (match) {
        const [, day, month, year] = match;
        return new Date(parseInt(year), parseInt(month) - 1, parseInt(day), 12, 0, 0);
      }
      return null;
    };
    
    const formatDateForInput = (date) => {
      if (!date) return '';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${day}.${month}.${year}`;
    };
    
    const txDates = transactions.map(t => parseDate(t.date)).filter(d => d !== null);
    const minDate = txDates.length > 0 ? new Date(Math.min(...txDates)) : new Date();
    const maxDate = txDates.length > 0 ? new Date(Math.max(...txDates)) : new Date();
    
    // Use user-selected dates if set, otherwise use calculated range
    const effectiveDateFrom = dateFrom || formatDateForInput(minDate);
    const effectiveDateTo = dateTo || formatDateForInput(maxDate);
    
    // Update state with calculated dates if not set
    if (!dateFrom) setDateFrom(formatDateForInput(minDate));
    if (!dateTo) setDateTo(formatDateForInput(maxDate));
    
    const data = {
      id: selectedProfile.id,
      oderId: selectedProfile.ozon_id || Math.floor(100000 + Math.random() * 900000),
      userId: selectedProfile.ozon_id || Math.floor(100000 + Math.random() * 900000),
      firstName,
      lastName,
      fullName: `${firstName} ${lastName}`,
      username: `${firstName.toLowerCase()}_trader`,
      phone,
      email,
      balance: selectedProfile.template_balance || 0,
      trades: generatedTrades,
      transactions: transactions,
      dateFrom: effectiveDateFrom,
      dateTo: effectiveDateTo,
      stats: {
        totalDeposits,
        totalWithdrawals,
        transactionCount: transactions.length
      },
      verified: true,
      totalTrades: generatedTrades.length,
      successRate: '100%',
      showBrowser
    };
    
    // Store data in localStorage for preview pages
    localStorage.setItem('bybit_preview_data', JSON.stringify(data));
    
    setGeneratedData(data);
    setIsGenerating(false);
    toast.success("Данные сгенерированы!");
  };

  const downloadArchive = async () => {
    if (!generatedData) return;
    
    setIsDownloadingArchive(true);
    toast.info("Создание архива скриншотов...");
    
    try {
      const response = await axios.post(
        `${API}/templates/bybit/archive`,
        { 
          data: generatedData,
          pages: ['main', 'trades', 'wallet', 'profile']
        },
        { 
          headers: getAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Bybit_${generatedData.bybitId}_${Date.now()}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Архив скриншотов скачан!");
    } catch (error) {
      console.error("Archive error:", error);
      toast.error("Ошибка создания архива");
    } finally {
      setIsDownloadingArchive(false);
    }
  };

  const getPreviewUrl = () => {
    if (!generatedData) return '';
    // Store data in localStorage to avoid URL length limits
    const storageKey = `bybit_preview_${Date.now()}`;
    localStorage.setItem(storageKey, JSON.stringify({...generatedData, showBrowser}));
    // Also store in standard key for backward compatibility
    localStorage.setItem('bybit_preview_data', JSON.stringify({...generatedData, showBrowser}));
    const browserParam = showBrowser ? 'true' : 'false';
    return `${window.location.origin}/templates/bybit/${selectedTemplate}?storageKey=${storageKey}&showBrowser=${browserParam}`;
  };

  const openPreview = () => {
    if (!generatedData) return;
    // Store data in localStorage
    localStorage.setItem('bybit_preview_data', JSON.stringify({...generatedData, showBrowser}));
    const browserParam = showBrowser ? 'true' : 'false';
    const url = `${window.location.origin}/templates/bybit/${selectedTemplate}?showBrowser=${browserParam}`;
    window.open(url, '_blank');
  };

  const copyLink = () => {
    const url = getPreviewUrl();
    if (url) {
      navigator.clipboard.writeText(url);
      toast.success("Ссылка скопирована!");
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="bybit-template-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center shadow-lg">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            Крипто P2P Биржа
          </h1>
          <p className="text-slate-500 mt-1">
            Генератор P2P биржи с историей сделок и перепиской
          </p>
        </div>
        <Button onClick={fetchProfiles} variant="outline">
          <RefreshCw className="w-4 h-4" />
          Обновить
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Panel */}
        <Card className="lg:col-span-1">
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-4">Настройки</h2>
            
            {/* Template Selection */}
            <div className="space-y-3 mb-6">
              <Label>Страница</Label>
              <div className="grid grid-cols-4 gap-2">
                {TEMPLATES.map((template) => {
                  const Icon = template.icon;
                  return (
                    <button
                      key={template.id}
                      onClick={() => setSelectedTemplate(template.id)}
                      className={`p-3 rounded-lg border-2 transition-all text-left ${
                        selectedTemplate === template.id
                          ? 'border-amber-500 bg-amber-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <Icon className={`w-5 h-5 mb-1 ${selectedTemplate === template.id ? 'text-amber-500' : 'text-slate-400'}`} />
                      <div className={`text-sm font-medium ${selectedTemplate === template.id ? 'text-amber-700' : 'text-slate-700'}`}>
                        {template.name}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Show Browser Option */}
            <div className="mb-6">
              <label className="flex items-center gap-3 p-3 rounded-lg border-2 border-slate-200 bg-white hover:border-amber-300 cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  checked={showBrowser}
                  onChange={(e) => {
                    setShowBrowser(e.target.checked);
                    setGeneratedData(null);
                  }}
                  className="w-5 h-5 rounded border-slate-300 text-amber-500 focus:ring-amber-500"
                />
                <div>
                  <div className="font-medium text-slate-700">Показывать рамку браузера</div>
                  <div className="text-xs text-slate-500">
                    Отображать Chrome-рамку с вкладками и адресной строкой
                  </div>
                </div>
              </label>
            </div>

            {/* Random Operations Option */}
            <div className="mb-6">
              <label className="flex items-center gap-3 p-3 rounded-lg border-2 border-slate-200 bg-white hover:border-amber-300 cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  checked={addRandomOperations}
                  onChange={(e) => {
                    const newValue = e.target.checked;
                    setAddRandomOperations(newValue);
                    setGeneratedData(null);
                    // Regenerate transactions with new setting
                    if (selectedProfile) {
                      const mixedTransactions = generateTransactions(selectedProfile, newValue);
                      setTransactions(mixedTransactions);
                    }
                  }}
                  className="w-5 h-5 rounded border-slate-300 text-amber-500 focus:ring-amber-500"
                />
                <div>
                  <div className="font-medium text-slate-700">Рандомные операции</div>
                  <div className="text-xs text-slate-500">
                    Добавить 3-6 случайных операций (ввод и вывод), первая снизу - положительная до 6000₽
                  </div>
                </div>
              </label>
            </div>

            {/* Date Filter */}
            <div className="mb-6 p-4 rounded-lg border-2 border-slate-200 bg-white">
              <div className="font-medium text-slate-700 mb-3">Фильтр дат (для Кошелька)</div>
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <label className="text-xs text-slate-500 mb-1 block">С (ДД.ММ.ГГГГ)</label>
                  <Input
                    type="text"
                    placeholder="ДД.ММ.ГГГГ"
                    value={dateFrom}
                    onChange={(e) => {
                      // Allow only digits and dots, auto-format as DD.MM.YYYY
                      let val = e.target.value.replace(/[^\d.]/g, '');
                      // Auto-add dots after day and month
                      if (val.length === 2 && !val.includes('.')) val += '.';
                      if (val.length === 5 && val.split('.').length === 2) val += '.';
                      if (val.length <= 10) {
                        setDateFrom(val);
                        setGeneratedData(null);
                      }
                    }}
                    className="w-full"
                  />
                </div>
                <div className="flex-1">
                  <label className="text-xs text-slate-500 mb-1 block">По (ДД.ММ.ГГГГ)</label>
                  <Input
                    type="text"
                    placeholder="ДД.ММ.ГГГГ"
                    value={dateTo}
                    onChange={(e) => {
                      // Allow only digits and dots, auto-format as DD.MM.YYYY
                      let val = e.target.value.replace(/[^\d.]/g, '');
                      // Auto-add dots after day and month
                      if (val.length === 2 && !val.includes('.')) val += '.';
                      if (val.length === 5 && val.split('.').length === 2) val += '.';
                      if (val.length <= 10) {
                        setDateTo(val);
                        setGeneratedData(null);
                      }
                    }}
                    className="w-full"
                  />
                </div>
              </div>
              <div className="text-xs text-slate-400 mt-2">
                Оставьте пустым для автоматического определения диапазона из транзакций
              </div>
            </div>

            {/* Profile Selection */}
            <div className="space-y-3 mb-6">
              <Label>Профиль OZON (основной пользователь)</Label>
              <div className="relative">
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="w-full p-3 rounded-lg border-2 border-slate-200 bg-white text-left flex items-center justify-between hover:border-slate-300"
                >
                  {selectedProfile ? (
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                        <User className="w-4 h-4 text-amber-600" />
                      </div>
                      <div>
                        <div className="text-slate-800 font-medium">{selectedProfile.name}</div>
                        <div className="text-xs text-slate-500">ID: {selectedProfile.ozon_id}</div>
                      </div>
                    </div>
                  ) : (
                    <span className="text-slate-400">Выберите профиль...</span>
                  )}
                  <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {dropdownOpen && (
                  <div className="absolute z-50 w-full mt-2 py-2 bg-white border rounded-lg shadow-xl max-h-48 overflow-y-auto">
                    {profiles.map((profile) => (
                      <button
                        key={profile.id}
                        onClick={() => handleSelectProfile(profile)}
                        className="w-full px-4 py-2 flex items-center gap-3 hover:bg-slate-50"
                      >
                        <User className="w-4 h-4 text-slate-400" />
                        <span>{profile.name}</span>
                        {selectedProfile?.id === profile.id && <Check className="w-4 h-4 text-amber-500 ml-auto" />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <Button
              onClick={handleGenerate}
              disabled={!selectedProfile || isGenerating}
              className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
            >
              {isGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <TrendingUp className="w-4 h-4" />}
              Сформировать
            </Button>
          </CardContent>
        </Card>

        {/* Right Panel - Trades Editor */}
        <Card className="lg:col-span-1">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-800">Сделки и переписки</h2>
              <Button size="sm" variant="outline" onClick={addTrade} disabled={trades.length >= 10}>
                <Plus className="w-4 h-4 mr-1" /> Добавить сделку
              </Button>
            </div>
            
            <div className="space-y-4 max-h-[500px] overflow-y-auto">
              {trades.map((trade, tradeIndex) => (
                <div key={trade.id} className="p-4 bg-slate-50 rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-slate-700">Сделка #{tradeIndex + 1}</span>
                    {trades.length > 1 && (
                      <Button size="sm" variant="ghost" onClick={() => removeTrade(trade.id)} className="text-red-500">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      placeholder="Имя партнёра"
                      value={trade.partnerName}
                      onChange={(e) => updateTrade(trade.id, 'partnerName', e.target.value)}
                    />
                    <Input
                      placeholder="Сумма ₽"
                      value={trade.amount}
                      onChange={(e) => updateTrade(trade.id, 'amount', e.target.value)}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs">Переписка</Label>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => addMessage(trade.id, 'partner')} className="text-xs h-6 px-2">
                          + Партнёр
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => addMessage(trade.id, 'me')} className="text-xs h-6 px-2">
                          + Я
                        </Button>
                      </div>
                    </div>
                    {trade.messages.map((msg, msgIndex) => (
                      <div key={msgIndex} className="flex gap-2 items-start">
                        <span className={`text-xs px-2 py-1 rounded ${msg.from === 'me' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
                          {msg.from === 'me' ? 'Я' : 'П'}
                        </span>
                        <Input
                          value={msg.text}
                          onChange={(e) => updateTradeMessage(trade.id, msgIndex, e.target.value)}
                          className="flex-1 text-sm"
                          placeholder="Сообщение..."
                        />
                        {trade.messages.length > 1 && (
                          <Button size="sm" variant="ghost" onClick={() => removeMessage(trade.id, msgIndex)} className="text-red-400 h-8 w-8 p-0">
                            ×
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Preview Panel */}
        {generatedData && (
          <Card className="lg:col-span-2">
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold text-slate-800 mb-4">Предпросмотр</h2>
              <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                  <span className="text-white font-bold">{generatedData.firstName[0]}{generatedData.lastName[0]}</span>
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-slate-800">{generatedData.fullName}</div>
                  <div className="text-sm text-slate-500">@{generatedData.username} • {generatedData.totalTrades} сделок • {generatedData.successRate} успех</div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={openPreview} className="bg-amber-500 hover:bg-amber-600">
                    <ExternalLink className="w-4 h-4 mr-1" /> Открыть
                  </Button>
                  <Button variant="outline" onClick={copyLink}>
                    <Copy className="w-4 h-4 mr-1" /> Копировать
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={downloadArchive}
                    disabled={isDownloadingArchive}
                  >
                    {isDownloadingArchive ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-1" />
                    )}
                    Архив
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
